import React, { useState } from 'react'
import { Paper, Typography, Collapse, withStyles, createStyles, Theme } from '@material-ui/core'
import { CheckCircle, Error, Info, Warning } from '@material-ui/icons'

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success'
export interface CustomAlertProps {
  severity?: AlertSeverity
  variant?: 'filled' | 'outlined' | 'standard'
  onClose?: () => void
  action?: React.ReactNode
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  elevation?: number
  square?: boolean
  closeText?: string
  role?: string
  'aria-describedby'?: string
  'aria-label'?: string
  title?: string
}
const createAlertStyles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      padding: theme.spacing.unit * 2,
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      alignItems: 'flex-start',
      '& .MuiAlert-icon': {
        marginRight: theme.spacing.unit,
        marginTop: '2px',
        flexShrink: 0,
      },
      '& .MuiAlert-message': {
        flex: 1,
        minWidth: 0,
      },
      '& .MuiAlert-action': {
        marginLeft: theme.spacing.unit,
        marginTop: '-2px',
        marginBottom: '-2px',
        flexShrink: 0,
      },
    },
    standardSuccess: {
      color: '#2e7d32 !important',
      backgroundColor: '#e8f5e8 !important',
      '& .MuiAlert-icon': {
        color: '#2e7d32 !important',
      },
      '& .MuiAlert-title': {
        color: '#2e7d32 !important',
      },
    },
    standardInfo: {
      color: '#0288d1 !important',
      backgroundColor: '#e3f2fd !important',
      '& .MuiAlert-icon': {
        color: '#0288d1 !important',
      },
      '& .MuiAlert-title': {
        color: '#0288d1 !important',
      },
    },
    standardWarning: {
      color: '#ed6c02 !important',
      backgroundColor: '#fff4e5 !important',
      '& .MuiAlert-icon': {
        color: '#ed6c02 !important',
      },
      '& .MuiAlert-title': {
        color: '#ed6c02 !important',
      },
    },
    standardError: {
      color: '#d32f2f !important',
      backgroundColor: '#ffebee !important',
      '& .MuiAlert-icon': {
        color: '#d32f2f !important',
      },
      '& .MuiAlert-title': {
        color: '#d32f2f !important',
      },
    },
    filledSuccess: {
      backgroundColor: '#2e7d32 !important',
      color: '#ffffff !important',
      '& .MuiAlert-icon': {
        color: '#ffffff !important',
      },
      '& .MuiAlert-title': {
        color: '#ffffff !important',
      },
    },
    filledInfo: {
      backgroundColor: '#0288d1 !important',
      color: '#ffffff !important',
      '& .MuiAlert-icon': {
        color: '#ffffff !important',
      },
      '& .MuiAlert-title': {
        color: '#ffffff !important',
      },
    },
    filledWarning: {
      backgroundColor: 'rgb(255, 244, 229) !important',
      color: 'rgb(102, 60, 0) !important',
      '& .MuiAlert-icon': {
        color: 'rgb(102, 60, 0) !important',
      },
      '& .MuiAlert-title': {
        color: 'rgb(102, 60, 0) !important',
      },
    },
    filledError: {
      backgroundColor: '#d32f2f !important',
      color: '#ffffff !important',
      '& .MuiAlert-icon': {
        color: '#ffffff !important',
      },
      '& .MuiAlert-title': {
        color: '#ffffff !important',
      },
    },
    outlinedSuccess: {
      color: '#2e7d32 !important',
      border: '1px solid #2e7d32 !important',
      backgroundColor: 'transparent !important',
      '& .MuiAlert-icon': {
        color: '#2e7d32 !important',
      },
      '& .MuiAlert-title': {
        color: '#2e7d32 !important',
      },
    },
    outlinedInfo: {
      color: '#0288d1 !important',
      border: '1px solid #0288d1 !important',
      backgroundColor: 'transparent !important',
      '& .MuiAlert-icon': {
        color: '#0288d1 !important',
      },
      '& .MuiAlert-title': {
        color: '#0288d1 !important',
      },
    },
    outlinedWarning: {
      color: '#ed6c02 !important',
      border: '1px solid #ed6c02 !important',
      backgroundColor: 'transparent !important',
      '& .MuiAlert-icon': {
        color: '#ed6c02 !important',
      },
      '& .MuiAlert-title': {
        color: '#ed6c02 !important',
      },
    },
    outlinedError: {
      color: '#d32f2f !important',
      border: '1px solid #d32f2f !important',
      backgroundColor: 'transparent !important',
      '& .MuiAlert-icon': {
        color: '#d32f2f !important',
      },
      '& .MuiAlert-title': {
        color: '#d32f2f !important',
      },
    },
    icon: {
      fontSize: 22,
    },
    message: {
      display: 'flex',
      alignItems: 'center',
      '& .MuiTypography-root': {
        margin: 0,
      },
    },
    title: {
      fontWeight: 500,
      marginBottom: theme.spacing.unit,
    },
    action: {
      display: 'flex',
      alignItems: 'flex-start',
    },
    closeButton: {
      padding: 4,
      marginLeft: theme.spacing.unit,
      marginTop: -2,
      marginBottom: -2,
    },
  })

const defaultIcons = {
  success: <CheckCircle />,
  info: <Info />,
  warning: <Warning />,
  error: <Error />,
}

const StyledAlert = withStyles(createAlertStyles)(({ classes, ...props }: any) => (
  <Paper {...props} className={classes.root} />
))

export const CustomAlert: React.FC<CustomAlertProps> = ({
  severity = 'info',
  variant = 'standard',
  onClose,
  action,
  icon,
  children,
  className = '',
  style,
  elevation = 0,
  square = false,
  closeText = 'Close',
  role = 'alert',
  'aria-describedby': ariaDescribedby,
  'aria-label': ariaLabel,
  title,
}) => {
  const [open, setOpen] = useState(true)

  const handleClose = () => {
    setOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const alertIcon = icon || defaultIcons[severity]

  const AlertWithStyles = withStyles(createAlertStyles)(({ classes, ...props }: any) => (
    <Paper
      {...props}
      className={`${classes.root} ${
        classes[`${variant}${severity.charAt(0).toUpperCase() + severity.slice(1)}`]
      } ${className}`}
    />
  ))

  const alertContent = (
    <AlertWithStyles
      style={style}
      elevation={elevation}
      square={square}
      role={role}
      aria-describedby={ariaDescribedby}
      aria-label={ariaLabel}
    >
      {alertIcon && (
        <div className="MuiAlert-icon">
          {React.cloneElement(alertIcon as React.ReactElement, {
            className: 'MuiAlert-icon',
          })}
        </div>
      )}

      <div className="MuiAlert-message">
        {title && (
          <Typography
            style={{ fontWeight: 'bold', fontSize: '14px' }}
            variant="headline"
            className="MuiAlert-title"
          >
            {title}
          </Typography>
        )}
        <div className="MuiAlert-message">
          {typeof children === 'string' ? (
            <Typography variant="body2">{children}</Typography>
          ) : (
            children
          )}
        </div>
      </div>
    </AlertWithStyles>
  )

  return onClose ? <Collapse in={open}>{alertContent}</Collapse> : alertContent
}

export default CustomAlert



// WEBPACK FOOTER //
// ./src/components/common/CustomAlert/index.tsx